import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Paper,
  Grid,
  CircularProgress,
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import {
  useGetCustomerQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
} from '../../../store/api/salesApi';

const initialFormData = {
  pass: '',
  name: '',
  email: '',
  code: '',
  phone: '',
  address: '',
  trn: '',
  creditLimit: 0,
  openingBalance: 0,
  latitude: '',
  longitude: '',
  locationUrl: '',
};

export function CustomerFormContent({
  customer = null,
  isEdit = false,
  onSuccess,
  onCancel,
  loading = false,
  compact = false,
}) {
  const [createCustomer] = useCreateCustomerMutation();
  const [updateCustomer] = useUpdateCustomerMutation();
  const [formData, setFormData] = useState(initialFormData);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (customer) {
      setFormData({
        pass: '',
        name: customer.name || '',
        email: customer.email || '',
        code: customer.code || '',
        phone: customer.phone || '',
        address: customer.address || '',
        trn: customer.trn || '',
        creditLimit: customer.creditLimit ?? 0,
        openingBalance: customer.openingBalance ?? 0,
        latitude: customer.latitude != null ? String(customer.latitude) : '',
        longitude: customer.longitude != null ? String(customer.longitude) : '',
        locationUrl: customer.locationUrl || '',
      });
    } else if (!isEdit) {
      setFormData(initialFormData);
    }
  }, [customer, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setSubmitError(null);
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setSubmitError('Geolocation is not supported in this browser.');
      return;
    }
    setSubmitError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const url = `https://www.google.com/maps?q=${lat},${lng}`;
        setFormData((prev) => ({
          ...prev,
          latitude: lat.toFixed(7),
          longitude: lng.toFixed(7),
          locationUrl: prev.locationUrl || url,
        }));
      },
      (err) => {
        setSubmitError(err.message || 'Could not get current location.');
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    try {
      const payload = {
        name: formData.name,
        email: formData.email || undefined,
        code: formData.code || undefined,
        phone: formData.phone || undefined,
        address: formData.address || undefined,
        trn: formData.trn?.trim() || undefined,
        creditLimit: Number(formData.creditLimit) || 0,
        openingBalance: Number(formData.openingBalance) || 0,
        latitude: formData.latitude ? Number(formData.latitude) : undefined,
        longitude: formData.longitude ? Number(formData.longitude) : undefined,
        locationUrl: formData.locationUrl?.trim() || undefined,
      };
      let result;
      if (isEdit) {
        if (formData.pass) payload.pass = formData.pass;
        result = await updateCustomer({ id: customer.id, ...payload }).unwrap();
      } else {
        result = await createCustomer({
          pass: formData.pass || undefined,
          ...payload,
        }).unwrap();
      }
      onSuccess?.(result);
    } catch (err) {
      setSubmitError(err?.data?.message || 'Error saving customer');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2} sx={{ overflow: 'visible' }}>
        <Grid size={{ xs: 12, md: 6 }} sx={{ overflow: 'visible' }}>
          <TextField
            fullWidth
            label="Customer Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{
              sx: { overflow: 'visible', whiteSpace: 'nowrap' },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={{ overflow: 'visible' }}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{
              sx: { overflow: 'visible', whiteSpace: 'nowrap' },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Customer Code"
            name="code"
            value={formData.code}
            onChange={handleChange}
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{ sx: { overflow: 'visible', whiteSpace: 'nowrap' } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{ sx: { overflow: 'visible', whiteSpace: 'nowrap' } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Customer TRN (if VAT registered)"
            name="trn"
            value={formData.trn}
            onChange={handleChange}
            size={compact ? 'small' : 'medium'}
            placeholder="Tax Registration Number"
            InputLabelProps={{ sx: { overflow: 'visible', whiteSpace: 'nowrap' } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Credit Limit"
            name="creditLimit"
            type="number"
            value={formData.creditLimit}
            onChange={handleChange}
            inputProps={{ min: 0, step: 0.01 }}
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{ sx: { overflow: 'visible', whiteSpace: 'nowrap' } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Opening Balance"
            name="openingBalance"
            type="number"
            value={formData.openingBalance}
            onChange={handleChange}
            inputProps={{ step: 0.01 }}
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{ sx: { overflow: 'visible', whiteSpace: 'nowrap' } }}
            helperText="Positive value means they owe us (Receivable)"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Location URL"
            name="locationUrl"
            value={formData.locationUrl}
            onChange={handleChange}
            placeholder="Paste map link (e.g. Google Maps)"
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{ sx: { overflow: 'visible', whiteSpace: 'nowrap' } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label="Latitude"
              name="latitude"
              value={formData.latitude}
              onChange={handleChange}
              size={compact ? 'small' : 'medium'}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Longitude"
              name="longitude"
              value={formData.longitude}
              onChange={handleChange}
              size={compact ? 'small' : 'medium'}
              sx={{ flex: 1 }}
            />
            <IconButton
              type="button"
              color="primary"
              onClick={handleUseCurrentLocation}
              sx={{ flexShrink: 0 }}
              title="Use current location"
            >
              <MyLocationIcon />
            </IconButton>
          </Box>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            multiline
            rows={compact ? 2 : 3}
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{ sx: { overflow: 'visible', whiteSpace: 'nowrap' } }}
          />
        </Grid>
        {submitError && (
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color="error">
              {submitError}
            </Typography>
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button type="button" variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
}

function CustomerForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { data: customer, isLoading } = useGetCustomerQuery(id, { skip: !isEdit });

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        {isEdit ? 'Edit Customer' : 'New Customer'}
      </Typography>
      <Paper sx={{ p: 3 }}>
        <CustomerFormContent
          customer={customer}
          isEdit={isEdit}
          loading={isEdit && isLoading}
          onSuccess={() => navigate('/customers')}
          onCancel={() => navigate('/customers')}
        />
      </Paper>
    </Box>
  );
}

export default CustomerForm;
